import * as React from 'react';
import clsx from 'clsx';
import { styled } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Typography, type TypographyProps } from 'src/components/Typography';

import type { FileUploaderProps } from 'src/components/FileUploader/FileUploader';
import fileUploaderClasses from 'src/components/FileUploader/fileUploaderClasses';

type FileUploaderLegendOwnProps = {
  /**
   * @access package
   */
  descriptionTextId?: string;
};

type FileUploaderLegendProps = React.HTMLAttributes<HTMLLegendElement> &
  Pick<TypographyProps, 'sx'> &
  Required<
    Pick<
      FileUploaderProps,
      'titleText' | 'descriptionText' | 'hideTitleText' | 'hideDescriptionText'
    >
  > &
  FileUploaderLegendOwnProps;

const FileUploaderLegendRoot = styled(Typography, {
  name: 'UnifyFileUploader',
  slot: 'Dropzone',
  overridesResolver: (props: FileUploaderLegendRootProps, styles) => {
    return [
      styles.legend,
      {
        [`.${fileUploaderClasses.legendTitle}`]: styles.legendTitle,
      },
      {
        [`.${fileUploaderClasses.legendDescription}`]: styles.legendDescription,
      },
    ];
  },
})<FileUploaderLegendRootProps>(({ ownerState, theme }) => {
  const { hideTitleText, hideDescriptionText } = ownerState;

  return {
    color: 'inherit', // Override web-skin `legend` element style

    ...(hideTitleText &&
      !hideDescriptionText && {
        [`.${fileUploaderClasses.legendTitle}`]: visuallyHidden,
      }),

    [`.${fileUploaderClasses.legendDescription}`]: {
      ...(hideDescriptionText && !hideTitleText
        ? visuallyHidden
        : {
            ...(!hideTitleText && { paddingTop: theme.spacing(1) }),
            display: 'block',
          }),
    },
  };
});

type FileUploaderLegendRootProps = {
  ownerState: Pick<
    FileUploaderLegendProps,
    'hideTitleText' | 'hideDescriptionText'
  >;
};

export const FileUploaderLegend = React.memo(function FileUploaderLegend(
  props: FileUploaderLegendProps
): React.ReactElement {
  // FIXME (adl): Need to intl the title text
  const {
    titleText,
    hideTitleText,
    descriptionText,
    hideDescriptionText,
    descriptionTextId,
    ...rest
  } = props;

  return (
    <FileUploaderLegendRoot
      component="legend"
      variant="h5"
      className={clsx(props.className, fileUploaderClasses.legend)}
      ownerState={{ hideTitleText, hideDescriptionText }}
      {...rest}
    >
      <span className={fileUploaderClasses.legendTitle}>{titleText}</span>
      <Typography
        id={descriptionTextId}
        className={fileUploaderClasses.legendDescription}
        component="small"
        variant="body1"
      >
        {descriptionText}
      </Typography>
    </FileUploaderLegendRoot>
  );
});

export default FileUploaderLegend;
